import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut } from "firebase/auth";
import { setDoc, doc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db } from "./firebaseConfig";
import { functions } from "./firebaseConfig";
import { logLogin, logLogout, logAdminAction, AUDIT_ACTIONS } from "../utils/auditLog";
import { encryptValue } from "../utils/encryption";

export const signUp = async (email, password, userInfo = null) => {
  try {
    console.log("Attempting to sign up with:", email);
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    console.log("Signup successful:", userCredential);
    
    // If userInfo is provided, create user document
    if (userInfo) {
      // Encrypt sensitive PII fields
      const encryptedMobile = userInfo.mobile ? await encryptValue(userInfo.mobile) : '';
      
      await setDoc(doc(db, "users", userCredential.user.uid), {
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
        email: userInfo.email.toLowerCase(),
        mobile: encryptedMobile,
        practice: userInfo.practice,
        role: userInfo.role,
        status: 'Active',
        created: new Date()
      });
      console.log("User document created successfully");
      
      // Log user creation for audit trail
      await logAdminAction(AUDIT_ACTIONS.USER_CREATED, userCredential.user.uid, {
        email: userInfo.email.toLowerCase(),
        role: userInfo.role
      });
    }
    
    return userCredential;
  } catch (error) {
    console.error("Error during signup:", error);
    throw error;
  }
};

export const logIn = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    console.log("Login successful");
    
    // Log successful login for audit trail
    await logLogin(true, email);
    
    return userCredential;
  } catch (error) {
    console.error("Error during login:", error);
    
    // Log failed login attempt for audit trail
    await logLogin(false, email, error.code || error.message);
    
    throw error;
  }
};

export const logOut = async () => {
  try {
    // Log logout for audit trail
    await logLogout();
    
    await signOut(auth);
  } catch (error) {
    throw error;
  }
};

export const setupPractice = async (practiceInfo, userId, email) => {
  try {
    // Encrypt sensitive PII fields
    const encryptedPhone = practiceInfo.officePhone ? await encryptValue(practiceInfo.officePhone) : '';
    const encryptedAddress = practiceInfo.officeAddress ? await encryptValue(practiceInfo.officeAddress) : '';
    
    await setDoc(doc(db, "practices", userId), {
      practiceName: practiceInfo.practiceName,
      officeAddress: encryptedAddress,
      officeCity: practiceInfo.officeCity,
      officeState: practiceInfo.officeState,
      officeZipCode: practiceInfo.officeZipCode,
      officePhone: encryptedPhone,
      email: email.toLowerCase(),
      status: 'Pending'
    });
    
    // Log practice creation for audit trail
    await logAdminAction(AUDIT_ACTIONS.CREATE, userId, {
      collection: 'practices',
      practiceName: practiceInfo.practiceName
    });
    
    alert('Signup successful.');
  } catch (error) {
    console.error("Error during practice setup:", error);
    throw error;
  }
};


export const resetPassword = async (email) => {
  try {
    const sendResetFn = httpsCallable(functions, 'sendPasswordResetEmailPublic');
    const result = await sendResetFn({
      email: email.toLowerCase().trim(),
      originUrl: window.location.origin
    });
    
    // Log password reset request for audit trail
    await logAdminAction(AUDIT_ACTIONS.PASSWORD_RESET, null, {
      email: email.toLowerCase()
    });
    
    return result?.data?.message || "If an account exists for that email, a reset link has been sent.";
  } catch (error) {
    console.error("Error sending password reset email:", error);
    throw error;
  }
};
